import { splitEncoding } from '@contentful/live-preview';
import Grid2 from '@mui/material/Unstable_Grid2';

import Button, {
  ButtonIconPosition,
  ButtonSize,
  ButtonVariant,
} from '@/components/BaseLibrary/Button/Button';
import Eyebrow from '@/components/Eyebrow/Eyebrow';
import { IconName } from '@/components/Icon/Icon';
import { ImageSources } from '@/components/Image/ArtDirectedImage';
import GridContainer2 from '@/components/Layout/GridContainer2/GridContainer2';
import Link from '@/components/Link/Link';
import MediaOverlay from '@/components/MediaOverlay/MediaOverlay';
import {
  Contentful,
  ContentStyle,
  FrontifyImageContent,
  LinkContent,
} from '@/contentful';
import useGlobalStyles from '@/styles/global.styles';
import { ViewportSize } from '@/styles/viewportSize';

import useStyles from './HeroFullOverlay.styles';

export enum HeroFullOverlayHeightVariant {
  MINIMAL = 'Minimal',
  TALL = 'Tall',
}

export interface HeroFullOverlayContent extends Contentful {
  fields: {
    backgroundImages?: FrontifyImageContent[];
    backgroundVideos?: string[];
    style?: ContentStyle;
    immersive?: boolean;
    applyDarkOverlay?: boolean;
    heightVariant?:
      | HeroFullOverlayHeightVariant.MINIMAL
      | HeroFullOverlayHeightVariant.TALL;
    backButton?: LinkContent;
    horizontalAlignment?: 'Left' | 'Center';
    verticalAlignment?: 'Top' | 'Bottom' | 'Center';
    eyebrow?: string;
    heading?: string;
    subheading?: string;
    primaryCta?: LinkContent;
    secondaryCta?: LinkContent;
    bulletedList?: string[];
    termsAndConditions?: LinkContent;
    showBackgroundImages?: boolean;
    showBackgroundVideos?: boolean;
  };
}

export interface HeroFullOverlayProps {
  content: HeroFullOverlayContent;
}

export default function HeroFullOverlay({ content }: HeroFullOverlayProps) {
  const {
    fields: {
      backgroundImages = [],
      backgroundVideos = [],
      style = ContentStyle.OnDark,
      immersive = true,
      applyDarkOverlay,
      heightVariant = 'Tall',
      backButton,
      horizontalAlignment = 'Left',
      verticalAlignment = 'Center',
      eyebrow,
      heading,
      subheading,
      primaryCta,
      secondaryCta,
      bulletedList,
      termsAndConditions,
    },
  } = content;

  const isMinimalVariant = heightVariant === 'Minimal';
  const isOnDark = style === ContentStyle.OnDark;
  const isCenterHorizontalAlignment = horizontalAlignment === 'Center';
  const { classes: globalClasses } = useGlobalStyles({ fixed: false });
  const { classes, cx } = useStyles();

  const images: ImageSources = {
    fallback: [backgroundImages[0], ViewportSize.xl],
    xs: [backgroundImages?.[2], ViewportSize.sm],
    sm: [backgroundImages?.[1], ViewportSize.md],
    md: [backgroundImages[0], ViewportSize.xl],
  };

  const videos = {
    fallback: backgroundVideos[0],
    xs: backgroundVideos[2],
    sm: backgroundVideos[1],
    md: backgroundVideos[0],
  };

  const containerClass = cx(
    classes.container,
    isOnDark ? classes.onDark : classes.onLight
  );

  return (
    <MediaOverlay
      images={images}
      videos={videos}
      overlayClassName={
        isMinimalVariant ? classes.minimal : classes.tallDefault
      }
      darken={applyDarkOverlay}
      className={containerClass}
    >
      <div
        className={cx(classes.contentContainer, {
          [classes.immersive]: immersive,
        })}
      >
        {backButton && (
          <GridContainer2
            gridProps={{
              className: verticalAlignment !== 'Top' ? classes.absolute : '',
            }}
          >
            <Grid2 xs={10} md={6}>
              <Button
                label={`Back to ${backButton.fields.text}`}
                href={backButton.fields.url}
                iconName={IconName.ARROW_LEFT}
                iconPosition={ButtonIconPosition.LEFT}
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.SM}
                isOnDark={isOnDark}
                className={classes.backButton}
              />
            </Grid2>
          </GridContainer2>
        )}
        <div
          className={cx(
            classes.content,
            isCenterHorizontalAlignment && classes.centerText,
            classes[`verticalAlign${verticalAlignment}`]
          )}
        >
          <GridContainer2>
            <Grid2
              xs={10}
              md={6}
              xsOffset={isCenterHorizontalAlignment ? 1 : 0}
              mdOffset={isCenterHorizontalAlignment ? 3 : 0}
            >
              {eyebrow && <Eyebrow text={eyebrow} isOnDark={isOnDark} />}
              {heading && (
                <h1
                  className={cx(
                    globalClasses.h1,
                    classes.heading,
                    'primaryTextColor'
                  )}
                >
                  {heading}
                </h1>
              )}
            </Grid2>
          </GridContainer2>
          <GridContainer2>
            <Grid2
              xs={10}
              sm={6}
              md={4}
              xsOffset={isCenterHorizontalAlignment ? 1 : 0}
              smOffset={isCenterHorizontalAlignment ? 3 : 0}
              mdOffset={isCenterHorizontalAlignment ? 4 : 0}
            >
              {subheading && (
                <p
                  className={cx(
                    classes.subheading,
                    globalClasses.body2,
                    'accentTextColor'
                  )}
                >
                  {subheading}
                </p>
              )}
              {bulletedList && (
                <ul
                  className={cx(
                    classes.bulletedList,
                    globalClasses.body,
                    'accentTextColor'
                  )}
                >
                  {bulletedList.map((item, index) => {
                    const { cleaned } = splitEncoding(item);

                    return <li key={`${cleaned}-${index}`}>{cleaned}</li>;
                  })}
                </ul>
              )}
              {termsAndConditions && (
                <Link
                  className={cx(globalClasses.bodyHyperlink, 'accentTextColor')}
                  href={termsAndConditions.fields.url}
                >
                  {termsAndConditions.fields.text}
                </Link>
              )}
              {primaryCta && (
                <div
                  className={cx(
                    classes.ctas,
                    isCenterHorizontalAlignment && classes.verticalAlignCenter
                  )}
                >
                  <Button
                    isOnDark={isOnDark}
                    variant={ButtonVariant.SECONDARY}
                    label={primaryCta.fields.text}
                    href={primaryCta.fields.url}
                    className={classes.button}
                    videoUrl={primaryCta.fields.videoUrl}
                  />
                  {secondaryCta && (
                    <Button
                      isOnDark={isOnDark}
                      variant={ButtonVariant.TERTIARY}
                      label={secondaryCta.fields.text}
                      href={secondaryCta.fields.url}
                    />
                  )}
                </div>
              )}
            </Grid2>
          </GridContainer2>
        </div>
      </div>
    </MediaOverlay>
  );
}
